import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { ContentWrapper, Loader } from 'components';
import { PROFILE_ROUTES } from 'configs';
import {
  getAllSubscriptions,
  getCurrentUserSubscription,
} from 'store/actionCreators/subscriptions';
import {
  selectCurrentUserSubscriptionLoaded,
  selectIsSubscriptionLoading,
  selectIsSubscriptionRefreshing,
  selectTrialStartErrorKey,
} from 'store/selectors/subscriptions';
import { Headline } from 'pages/UserProfile/Header/styled';
import { Layout } from 'pages/UserProfileCommunities/styled';

import ChoosePaymentMethodFromUrl from './ChoosePaymentMethodFromUrl';
import FreeAccountView from './FreeAccountView';
import StripeCheckoutOutcome from './StripeCheckoutOutcome';
import SubscribedPlanView from './SubscribedPlanView';
import { useStripeCheckoutReturn } from './useStripeCheckoutReturn';
import { useSubscriptionProfileModel } from './useSubscriptionProfileModel';
import {
  CheckoutConfirmingDots,
  CheckoutConfirmingSpinnerRing,
  CheckoutConfirmingWrap,
  CheckoutOutcomeEnter,
  FloatingLoaderWrap,
  Main,
  OnboardingConfirmHint,
  OnboardingConfirmTitle,
  PageRoot,
} from './styled';

const UserProfileSubscription = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsSubscriptionLoading);
  const isRefreshingSubscription = useSelector(selectIsSubscriptionRefreshing);
  const subscriptionLoaded = useSelector(selectCurrentUserSubscriptionLoaded);
  const trialStartErrorKey = useSelector(selectTrialStartErrorKey);

  const model = useSubscriptionProfileModel();
  const {
    stripeOutcome,
    checkoutStripeFlow,
    sessionIdFromUrl,
    checkoutPlanLabel,
    onboardingOutcomePlanLabel,
    clearStripeQuery,
    showCheckoutConfirmingLoader,
    suppressFloatingRefetchLoader,
    subscriptionReturnExpectsSync,
  } = useStripeCheckoutReturn({
    subscription: model.subscription,
  });

  const changePlanPath = `/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`;
  const cancelSubscriptionPath = `/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CANCEL_SUBSCRIPTION}`;

  useEffect(() => {
    if (window) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    dispatch(getCurrentUserSubscription.trigger());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllSubscriptions.trigger({ silent: true }));
  }, [dispatch]);

  const showInitialLoader = isLoading && !subscriptionLoaded;
  const trialInlineError = trialStartErrorKey ? t(trialStartErrorKey) : null;

  return (
    <ContentWrapper minHeight="100svh">
      <PageRoot>
        {isRefreshingSubscription &&
          model.subscription?.id &&
          !suppressFloatingRefetchLoader && (
            <FloatingLoaderWrap>
              <Loader size="60px" type={null} />
            </FloatingLoaderWrap>
          )}

        <Layout.Header>
          <Headline>{t('subscriptions.profile.page.title')}</Headline>
        </Layout.Header>
        <Main>
          {stripeOutcome ? (
            showCheckoutConfirmingLoader ? (
              <CheckoutConfirmingWrap
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <CheckoutConfirmingSpinnerRing>
                  <Loader size="40px" type={null} />
                </CheckoutConfirmingSpinnerRing>
                <OnboardingConfirmTitle>
                  {checkoutStripeFlow === 'onboarding'
                    ? t('subscriptions.profile.confirming.onboardingTitle')
                    : t('subscriptions.profile.confirming.subscriptionTitle')}
                </OnboardingConfirmTitle>
                <OnboardingConfirmHint>
                  {checkoutStripeFlow === 'onboarding'
                    ? t('subscriptions.profile.confirming.onboardingHint')
                    : t('subscriptions.profile.confirming.subscriptionHint')}
                </OnboardingConfirmHint>
                <CheckoutConfirmingDots aria-hidden>
                  <span />
                  <span />
                  <span />
                </CheckoutConfirmingDots>
              </CheckoutConfirmingWrap>
            ) : (
              <CheckoutOutcomeEnter
                key={
                  checkoutStripeFlow === 'onboarding' && sessionIdFromUrl
                    ? `onb-${sessionIdFromUrl}`
                    : checkoutStripeFlow === 'subscription' &&
                      sessionIdFromUrl &&
                      subscriptionReturnExpectsSync
                    ? `sub-${sessionIdFromUrl}`
                    : 'stripe-outcome'
                }
              >
                <StripeCheckoutOutcome
                  variant={stripeOutcome}
                  sessionId={sessionIdFromUrl}
                  planLabel={
                    checkoutStripeFlow === 'onboarding'
                      ? onboardingOutcomePlanLabel
                      : checkoutPlanLabel
                  }
                  checkoutFlow={checkoutStripeFlow}
                  onDismiss={clearStripeQuery}
                />
              </CheckoutOutcomeEnter>
            )
          ) : showInitialLoader ? (
            <Loader />
          ) : !model.subscription?.id ? (
            <FreeAccountView
              model={model}
              profile={model.profile}
              trialInlineError={trialInlineError}
              changePlanPath={changePlanPath}
            />
          ) : (
            <SubscribedPlanView
              model={model}
              changePlanPath={changePlanPath}
              cancelSubscriptionPath={cancelSubscriptionPath}
            />
          )}
          <ChoosePaymentMethodFromUrl
            ready={
              subscriptionLoaded &&
              Boolean(model.subscription?.id) &&
              !stripeOutcome
            }
          />
        </Main>
      </PageRoot>
    </ContentWrapper>
  );
};

export default UserProfileSubscription;
